import type { App, Project } from '@hangar/shared';
import type { Profile, ProviderAccount } from '@/api/schemas';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { RowActions, type RowAction } from '@/components/RowActions';
import { DefinedInBadge, ExecutionModeTag, PlatformIcon, Tag } from '@/components/badges';
import { ProviderBadge } from '@/components/ProviderBadge';
import { useSeesProviders } from '@/lib/useAudience';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { bindingEnvironment, bindingProfile, environmentName, profileProviderKind, profileSubtitle } from './profileListModel';
import { RunProfileButton } from './RunProfileButton';

interface Props {
  profiles: Profile[];
  app: App;
  project: Project;
  accounts: ProviderAccount[] | undefined;
  actionsFor: (profile: Profile) => RowAction[];
}

/**
 * Three columns: this card is the right column of the project page (570–820 px from 1025, full width below).
 * The platform icon leads the profile name; execution modes and environment bindings share one column.
 */
export function ProfilesDesktopTable({ profiles, app, project, accounts, actionsFor }: Props) {
  return (
    <div className="@container">
      <Table
        aria-label="Build profiles"
        className="table-fixed sm:[&_td]:px-3 sm:[&_th]:px-3 sm:[&_td:first-child]:pl-5 sm:[&_th:first-child]:pl-5 sm:[&_td:last-child]:pr-5 sm:[&_th:last-child]:pr-5"
      >
        <ProfilesTableHead />
        <TableBody>
          {profiles.map((p, i) => (
            <TableRow key={p.id} data-testid={`profile-row-${p.platform}-${p.slug}`} style={stagger(i)} className="enter">
              <ProfileCell p={p} accounts={accounts} />
              <ModesCell p={p} project={project} />
              <TableCell className="py-2.5 text-right">
                <span className="inline-flex items-center gap-1">
                  <RunProfileButton profile={p} app={app} project={project} compact />
                  <RowActions actions={actionsFor(p)} subject={p.name} variant="icon" />
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ProfilesTableHead() {
  return (
    <TableHeader>
      <TableRow className="hover:bg-transparent">
        <TableHead>Profile</TableHead>
        <TableHead className="w-[40%] whitespace-normal">Execution · environments</TableHead>
        <TableHead className="w-[196px] text-right @max-[36rem]:w-[156px]">
          <span className="sr-only">Actions</span>
        </TableHead>
      </TableRow>
    </TableHeader>
  );
}

function ProfileCell({ p, accounts }: { p: Profile; accounts: ProviderAccount[] | undefined }) {
  const sees = useSeesProviders();
  return (
    <TableCell className="overflow-hidden py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <PlatformIcon platform={p.platform} className="shrink-0" />
        <span className="truncate text-sm font-medium text-foreground" title={p.name}>
          {p.name}
        </span>
        {/* A narrow card (the 1025–1279 px right column) keeps only the lock of a hangar.yml profile. */}
        <DefinedInBadge definedIn={p.definedIn} className="@max-[36rem]:hidden" />
        {p.definedIn === 'config_file' ? <Icon name="lock" size={14} label="Managed by hangar.yml" className="hidden shrink-0 text-subtle @max-[36rem]:inline-block" /> : null}
      </div>
      <span className="mt-0.5 flex min-w-0 items-center gap-1.5 font-mono text-xs text-subtle">
        <ProviderBadge kind={profileProviderKind(p, accounts)} />
        <span className="truncate" title={sees ? (p.providerAccount?.name ?? undefined) : undefined}>
          {profileSubtitle(p)}
        </span>
      </span>
    </TableCell>
  );
}

function ModesCell({ p, project }: { p: Profile; project: Project }) {
  return (
    <TableCell className="overflow-hidden whitespace-normal py-2.5">
      <span className="flex flex-wrap gap-1">
        {p.executionModes.map((m) => (
          <ExecutionModeTag key={m} mode={m} />
        ))}
      </span>
      <span className="mt-1 flex flex-wrap gap-1 font-mono text-xs text-muted-foreground">
        {p.environments.length === 0 ? <span className="text-subtle">no bindings</span> : null}
        {p.environments.map((b) => (
          <Tag key={b.environmentId} className={cn('max-w-full', bindingEnvironment(project, b)?.kind === 'production' && 'text-env-production')}>
            <span className="truncate">
              {environmentName(project, b.environmentId)}
              {bindingProfile(b) ? ` → ${bindingProfile(b)}` : ''}
            </span>
          </Tag>
        ))}
      </span>
    </TableCell>
  );
}
